<template>
  <div :class="fieldGroupClasses">
    <slot name="field">
      <input required type="text" />
    </slot>
    <span class="academy-field-group__bar"></span>
    <label class="academy-field-group__label">{{ label }}</label>
    <!-- Message Validation -->
    <slot name="validation-message">
      <div v-if="validationMessage" class="academy-field-group__validation-message">
        <span>{{ validationMessage }}</span>
      </div>
    </slot>

    <!-- Icon Left -->
    <div v-if="iconLeft" class="academy-field-group__icon-left">
      <slot name="icon-left">
        <b-icon :icon="iconLeft" :size="iconSize"></b-icon>
      </slot>
    </div>
    <!-- Icon Right -->
    <div v-if="iconRight" class="academy-field-group__icon-right">
      <slot name="icon-right">
        <b-icon :icon="iconRight" :size="iconSize"></b-icon>
      </slot>
    </div>
  </div>
</template>

<script>
import cn from 'classnames'

export default {
  name: 'FieldGroup',
  props: {
    fieldGroupClass: {
      type: String,
      default() {
        return ''
      },
    },
    iconSize: {
      type: String,
      default() {
        return ''
      },
    },
    iconLeft: {
      type: String,
      default() {
        return ''
      },
    },
    iconRight: {
      type: String,
      default() {
        return ''
      },
    },
    label: {
      type: String,
      default() {
        return ''
      },
    },
    loading: {
      type: Boolean,
      default() {
        return false
      },
    },
    readonly: {
      type: Boolean,
      default() {
        return false
      },
    },
    typeMessage: {
      type: String,
      default() {
        return ''
      },
    },
    validationMessage: {
      type: String,
      default: '',
    },
  },
  computed: {
    fieldGroupClasses() {
      const loadingClass = this.loading ? 'is-loading' : ''
      const iconLeftClass = this.iconLeft ? 'has-icon-left' : ''
      const iconRightClass = this.iconRight ? 'has-icon-right' : ''
      const readonlyClass = this.readonly ? 'is-readonly' : ''
      return cn('academy-field-group', this.fieldGroupClass, loadingClass, iconLeftClass, iconRightClass, readonlyClass)
    },
  },
}
</script>

<style lang="scss">
.academy-field-group {
  width: 100%;
  position: relative;
  display: inline-block;
  margin-bottom: 45px;

  &.has-icon-left {
    input {
      padding-left: 2.25em;
    }
    .academy-field-group__label {
      left: 2em;
    }
  }

  &.has-icon-right,
  &.is-loading {
    input {
      padding-right: 2.25em;
    }
  }
  &.is-loading {
    &:after {
      position: absolute;
      right: 0.625em;
      top: 0.625em;
      z-index: 4;
      animation: spinAround 0.5s infinite linear;
      border: 2px solid $color-regular-grey;
      border-radius: 290486px;
      border-right-color: transparent;
      border-top-color: transparent;
      content: '';
      display: block;
      height: 1em;
      width: 1em;
    }
  }

  &__bar {
    position: relative;
    display: block;
    width: 100%;
  }
  &__bar:before,
  &__bar:after {
    content: '';
    height: 2px;
    width: 0;
    bottom: 0px;
    position: absolute;
    background: $color-bright-sky-blue-two;
    transition: 0.2s ease all;
    -moz-transition: 0.2s ease all;
    -webkit-transition: 0.2s ease all;
  }
  &__bar:before {
    left: 50%;
  }
  &__bar:after {
    right: 50%;
  }

  &__label {
    color: $color-soft-grey;
    font-size: 1.125em;
    font-weight: normal;
    position: absolute;
    pointer-events: none;
    left: 5px;
    top: 10px;
    transition: 0.2s ease all;
    -moz-transition: 0.2s ease all;
    -webkit-transition: 0.2s ease all;
  }
  &__icon-left,
  &__icon-right {
    color: $color-regular-grey;
    width: 2em;
    display: flex;
    align-items: center;
    font-size: 1.2em;
    position: absolute;
    top: 0;
    bottom: 0;
    z-index: 4;
    padding-top: 2px;
  }
  &__icon-left {
    left: 0;
  }
  &__icon-right {
    right: 0;
  }

  &__validation-message {
    font-size: 0.875em;
    position: absolute;
    top: 100%;
    right: 0;
    bottom: 0;
    left: 0;
    z-index: 4;
    padding-top: 3px;
    height: 30px;
    > span {
      color: $color-text-dark-light;
    }
  }

  &.is-readonly {
    input,
    .academy-custom-select,
    .academy-custom-datepicker {
      pointer-events: none !important;
      border-color: transparent !important;
    }
    .academy-field-group__label {
      top: -20px !important;
      left: 5px !important;
      font-size: 0.875em !important;
    }
  }

  input,
  textarea,
  .academy-custom-select {
    font-size: 1.125em;
    padding: 10px 10px 10px 5px;
    display: block;
    width: 100%;
    border: none !important;
    border-radius: 0;
    box-shadow: none !important;
    border-bottom: 1px solid $color-regular-grey !important;
    color: $color-dark-2;
    font-family: Nunito Sans;
  }

  input:focus,
  select:focus,
  textarea:focus,
  .academy-custom-select.is-active {
    outline: none;
    border-color: transparent;
    box-shadow: none;
  }

  textarea:focus ~ .academy-field-group__label,
  textarea:valid ~ .academy-field-group__label,
  input:-webkit-autofill ~ .academy-field-group__label,
  textarea:-webkit-autofill ~ .academy-field-group__label,
  .academy-custom-select.is-active ~ .academy-field-group__label {
    top: -20px;
    left: 5px;
    font-size: 0.875em;
  }

  input.academy-input--focused ~ .academy-field-group__label,
  input.academy-input--not-empty ~ .academy-field-group__label,
  textarea.academy-textarea--focused ~ .academy-field-group__label,
  textarea.academy-textarea--not-empty ~ .academy-field-group__label,
  .academy-custom-select.is-selected ~ .academy-field-group__label,
  .academy-custom-select.is-active ~ .academy-field-group__label,
  .academy-custom-datepicker.is-active ~ .academy-field-group__label,
  .autocomplete.academy-select-country.is-focused ~ .academy-field-group__label,
  .autocomplete.academy-select-country.is-active ~ .academy-field-group__label,
  .autocomplete.academy-select-country.is-not-empty ~ .academy-field-group__label,
  .autocomplete.academy-select-country.is-not-empty ~ .academy-field-group__label,
  ~ .academy-field-group__label {
    top: -20px;
    left: 5px;
    font-size: 0.875em;
  }

  input:focus ~ .academy-field-group__bar:before,
  input:focus ~ .academy-field-group__bar:after,
  textarea:focus ~ .academy-field-group__bar:before,
  textarea:focus ~ .academy-field-group__bar:after,
  .academy-custom-select.is-active ~ .academy-field-group__bar:before,
  .academy-custom-select.is-active ~ .academy-field-group__bar:after,
  .academy-custom-datepicker.is-focused ~ .academy-field-group__bar:before,
  .academy-custom-datepicker.is-focused ~ .academy-field-group__bar:after,
  .autocomplete.academy-select-country.is-focused ~ .academy-field-group__bar:before,
  .autocomplete.academy-select-country.is-focused ~ .academy-field-group__bar:after {
    width: 50%;
  }

  /* Change autocomplete styles in WebKit */
  input:-webkit-autofill,
  input:-webkit-autofill:hover,
  input:-webkit-autofill:focus,
  textarea:-webkit-autofill,
  textarea:-webkit-autofill:hover,
  textarea:-webkit-autofill:focus,
  select:-webkit-autofill,
  select:-webkit-autofill:hover,
  select:-webkit-autofill:focus {
    border-bottom: 1px solid $color-regular-grey;
    -webkit-text-fill-color: #363636;
    -webkit-box-shadow: 0 0 0px 1000px #fff inset;
    transition: background-color 5000s ease-in-out 0s;
  }

  /* Animation loading */
  @-webkit-keyframes spinAround {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(359deg);
    }
  }
  @-moz-keyframes spinAround {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(359deg);
    }
  }
  @keyframes spinAround {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(359deg);
    }
  }
}
</style>
